/* ============================================================
   <pdp-section-tabs> — sticky scroll-spy tab bar for the PDP.
   Each tab points to a section by anchor ID. JS handles:
     • click to scroll (with offset)
     • scroll-spy: active tab tracks the section currently in view
     • mobile auto-scroll: keeps the active tab in horizontal view
   ============================================================ */

class PdpSectionTabs extends HTMLElement {
  connectedCallback() {
    this.barEl    = this.querySelector('[data-pdp-tabs-bar]');
    this.innerEl  = this.querySelector('[data-pdp-tabs-inner]');
    this.tabEls   = Array.from(this.querySelectorAll('[data-pdp-tab]'));
    if (this.tabEls.length === 0) return;

    /* Resolve each tab to a target section by matching the tab's label
       against any element on the page carrying [data-scroll-tab-label="..."]
       (set by each PDP section's "Scroll tab label" setting). Tabs with
       no matching section hide automatically. */
    const resolved = this.tabEls.map((el) => ({
      el,
      raw: el.dataset.tabLabel,
      target: this._resolveTarget(el.dataset.tabLabel),
    }));
    resolved.forEach((t) => {
      if (!t.target) t.el.hidden = true;
    });
    this.tabs = resolved.filter((t) => t.target);

    if (this.tabs.length === 0) {
      const tabLabels = resolved.map((t) => `"${t.raw}"`).join(', ');
      const availableLabels = Array.from(document.querySelectorAll('[data-scroll-tab-label]'))
        .map((el) => `"${el.getAttribute('data-scroll-tab-label')}"`);
      console.warn(
        '[pdp-section-tabs] No tab resolved a target — bar will stay hidden.\n' +
          'Tab labels on this bar: ' + (tabLabels || '(none)') + '\n' +
          'Section "Scroll tab label" values found on page: ' +
          (availableLabels.length ? availableLabels.join(', ') : '(none — fill the field on each PDP section in the customizer)'),
      );
      return;
    }

    this._activeTab = null;
    this._isClickScrolling = false;
    this._clickScrollTimer = null;

    /* In customizer mode the merchant must see the bar inline so they can
       configure tabs. On the live site the bar is hidden until stuck. */
    if (window.Shopify && window.Shopify.designMode) {
      this.classList.add('is-design-mode');
    }

    this._bindClicks();
    this._bindScrollLoop();

    /* Re-evaluate offset when viewport crosses the desktop breakpoint. */
    this._mql = window.matchMedia('(min-width: 990px)');
    this._mql.addEventListener('change', () => this._applyStickyOffset());
    this._applyStickyOffset();
  }

  disconnectedCallback() {
    if (this._mql) this._mql.removeEventListener('change', this._applyStickyOffset);
    if (this._clickScrollTimer) clearTimeout(this._clickScrollTimer);
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      window.removeEventListener('resize', this._scrollHandler);
    }
  }

  /* ---- Target resolution ----
     Each PDP section that should be tab-targetable has a "Scroll tab label"
     text setting. The Liquid renders an invisible <span data-scroll-tab-label>
     marker carrying that value. To find a tab's target, we look up an element
     with the matching marker, then walk up to its enclosing .shopify-section
     wrapper — that's the element the bar scrolls to and scroll-spies. */
  _resolveTarget(label) {
    if (!label) return null;
    const trimmed = String(label).trim();
    if (!trimmed) return null;
    const marker = document.querySelector(`[data-scroll-tab-label="${CSS.escape(trimmed)}"]`);
    if (!marker) return null;
    return marker.closest('.shopify-section') || marker;
  }

  /* ---- Bar height ----
     Read from CSS variable rather than measuring the DOM, since the bar
     is `display: none` (visibility: hidden) until stuck and would
     otherwise report 0 height. */
  _getBarHeight() {
    const cs = getComputedStyle(this);
    const v = parseFloat(cs.getPropertyValue('--pdp-tabs-height'));
    return Number.isFinite(v) && v > 0 ? v : 50;
  }

  /* ---- Sticky offset ----
     --pdp-tabs-offset carries only the merchant-configured extra gap;
     the bar's CSS `top` adds the theme-maintained --height-header on top
     of it (live sticky-header height, 0px when the header isn't sticky).
     JS reads --height-header fresh wherever it does scroll math — it is
     set by theme.js after Alpine init and changes live (e.g. the
     reduce-logo-size header shrinks on scroll), so caching it here
     would let the .is-stuck toggle, the scroll-spy trigger line and
     click-scroll drift out of sync with the CSS position. */
  _getHeaderHeight() {
    const v = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--height-header'),
    );
    return Number.isFinite(v) ? v : 0;
  }

  _applyStickyOffset = () => {
    const isDesktop = this._mql ? this._mql.matches : window.matchMedia('(min-width: 990px)').matches;
    const extra = parseInt(
      isDesktop ? this.dataset.offsetDesktop : this.dataset.offsetMobile,
      10,
    ) || 0;
    this.style.setProperty('--pdp-tabs-offset', extra + 'px');
    this._stickyOffsetPx = extra;
  };

  /* ---- Click → scroll target into view (account for sticky bar height) ---- */
  _bindClicks() {
    this.tabs.forEach(({ el, target }) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this._scrollToTarget(target, el);
      });
    });
  }

  _scrollToTarget(target, tabEl) {
    const barHeight = this._getBarHeight();
    const marker = target.querySelector('[data-scroll-tab-label]');
    const extraOffset = marker ? parseInt(marker.dataset.scrollExtraOffset || '0', 10) : 0;
    const offset = this._getHeaderHeight() + (this._stickyOffsetPx || 0) + barHeight + extraOffset;
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset - offset - 8;

    /* Pause scroll-spy while we programmatically scroll, then re-arm. The
       intersection observer would otherwise toggle through every section
       we pass through and end on the wrong one. */
    this._isClickScrolling = true;
    this._setActive(tabEl);
    this._scrollActiveIntoView(tabEl);

    window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });

    if (this._clickScrollTimer) clearTimeout(this._clickScrollTimer);
    this._clickScrollTimer = setTimeout(() => {
      this._isClickScrolling = false;
    }, 700);
  }

  _setActive(tabEl) {
    if (this._activeTab === tabEl) return;
    if (this._activeTab) this._activeTab.classList.remove('is-active');
    tabEl.classList.add('is-active');
    this._activeTab = tabEl;
  }

  /* ---- Unified scroll loop ----
     One rAF-throttled scroll handler does two things:
       1. Toggles the .is-stuck class on the host so the bar paints fixed
          once the viewport top passes the host, staying stuck through every
          section below it.
       2. Picks the active tab purely from current scroll position —
          "deepest tracked section whose top is at or above the trigger
          line wins." This avoids cached-state bugs that occur with
          IntersectionObserver during programmatic click-scrolls.

     Customizer mode shows the bar inline always; we keep the active-tab
     loop running there too so the merchant sees correct highlighting
     while previewing.
  */
  _bindScrollLoop() {
    let raf = 0;
    const update = () => {
      raf = 0;
      const stickyOffset = this._getHeaderHeight() + (this._stickyOffsetPx || 0);
      const barHeight = this._getBarHeight();
      const activeTriggerY = stickyOffset + barHeight + 1;

      /* --- Sticky toggle (skipped in customizer mode) ---
         Once the host reaches the header line the bar stays stuck for the
         rest of the scroll, so it remains visible over every section below
         it — not just the tab-linked range. */
      if (!this.classList.contains('is-design-mode')) {
        const hostTop = this.getBoundingClientRect().top;
        this.classList.toggle('is-stuck', hostTop <= stickyOffset);
      }

      /* --- Active tab pick (skip during programmatic click-scroll) ---
         Rule: a tab is active only when the trigger line is physically
         inside its section (sectionTop ≤ triggerY ≤ sectionBottom).
         If the trigger is in a gap section that has no tab, all tabs
         are deactivated — nothing is highlighted. */
      if (!this._isClickScrolling) {
        let chosen = null;

        for (let i = 0; i < this.tabs.length; i++) {
          const rect = this.tabs[i].target.getBoundingClientRect();
          if (rect.bottom < activeTriggerY) continue; // fully scrolled past
          if (rect.top > activeTriggerY + barHeight + 32) break; // not reached yet (small tolerance for smooth-scroll landing)
          chosen = this.tabs[i]; // trigger line is inside this section
          break;
        }

        if (!chosen) {
          /* Viewport is in a gap — deactivate all tabs. */
          if (this._activeTab) {
            this._activeTab.classList.remove('is-active');
            this._activeTab = null;
          }
        } else if (chosen.el !== this._activeTab) {
          this._setActive(chosen.el);
          this._scrollActiveIntoView(chosen.el);
        }
      }
    };

    this._scrollHandler = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    window.addEventListener('resize', this._scrollHandler, { passive: true });
    update();
  }

  /* ---- Auto-scroll active tab into the bar's horizontal view (mobile) ---- */
  _scrollActiveIntoView(tabEl) {
    if (!this.innerEl) return;
    /* No-op on desktop where the bar doesn't scroll. */
    if (this.innerEl.scrollWidth <= this.innerEl.clientWidth) return;

    const tabRect = tabEl.getBoundingClientRect();
    const barRect = this.innerEl.getBoundingClientRect();

    /* If tab is fully visible inside the bar, nothing to do. */
    const fullyVisible =
      tabRect.left >= barRect.left + 8 && tabRect.right <= barRect.right - 8;
    if (fullyVisible) return;

    const tabCenter = tabEl.offsetLeft + tabEl.offsetWidth / 2;
    const target = tabCenter - this.innerEl.clientWidth / 2;
    this.innerEl.scrollTo({
      left: Math.max(0, target),
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('pdp-section-tabs')) {
  customElements.define('pdp-section-tabs', PdpSectionTabs);
}
